import styled, { css, keyframes } from 'styled-components';
import { selectColor } from '@panel/commons/core/theme';
import { select } from '@panel/commons/core/themev2';
import Minus from '@panel/commons/assets/icons/minus.svg';
import ArrowUp from '@panel/commons/assets/icons/arrow-up-green.svg';
import ArrowDown from '@panel/commons/assets/icons/arrow-down-red.svg';

const TableLayout = styled.div`
  width: 100%;
  .subtitle {
    margin-bottom: 12px;
  }
  .titleRow-container {
    text-transform: capitalize;
  }
  p {
    color: ${selectColor('grayDark')};
    font-weight: 500;
    margin-bottom: 30px;
  }
  table {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    border-radius: 10px;
    border: 1px solid ${selectColor('grayMedium')};
  }
  tr {
    height: 60px;
    background-color: ${selectColor('grayLight')};
    td,
    th {
      text-align: right;
    }
  }
  tr:nth-child(even) {
    background-color: white;
  }
  th:first-child,
  td:first-child {
    padding-left: 46px;
    text-align: left;
  }
  th:first-child {
    border-top-left-radius: 10px;
  }
  th:last-child {
    border-top-right-radius: 10px;
  }
  th {
    color: ${selectColor('grayDark')};
  }
  tr:last-child {
    td:last-child {
      border-bottom-right-radius: 10px;
    }
    td:first-child {
      border-bottom-left-radius: 10px;
    }
  }
  ${({ isMobile }) =>
    isMobile &&
    css`
      td:first-child {
        padding-left: 16px;
      }
      th:first-child {
        padding-left: 16px;
      }
    `}

  .headers {
    background-color: ${selectColor('grayMedium')};
  }
  .withHelper {
    display: flex;
    align-items: center;
    height: 60px;
    gap: 5px;
  }
  .helperDesktop {
    justify-content: center;
  }
`;

const Headers = styled.th`
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  white-space: break-spaces;
  ${({ specialWidth }) =>
    css`
      width: ${specialWidth ? '16%' : '28%'};
    `}
`;

const TableRow = styled.tr`
  td {
    span {
      font-weight: ${({ strong }) => (strong ? 500 : 400)};
    }
    font-weight: ${({ strong }) => (strong ? 600 : 400)};
  }
`;

const HeaderContent = styled.span`
  font-weight: ${({ strong }) => (strong ? 500 : 400)};
`;

const TableData = styled.td`
  font-weight: ${({ strong }) => (strong ? 500 : 400)} !important;
`;

const NoData = styled.div`
  white-space: break-spaces;
  font-size: 12px;
  color: ${selectColor('grayDark')};
`;

const RetryButton = styled.button`
  font-family: 'Hind';
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 19px;
  color: ${selectColor('reservedOrange')};
  background-color: none;
  border: none;
  cursor: pointer;
`;

const dotFlashing = keyframes`
  0% {
    opacity: 1;
  }
  50%{
    opacity: 0.4;
  }
  100% {
    opacity: 0.2;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const pillsColors = {
  ALTA: {
    color: 'snackGreen',
    background: 'positiveBack',
    icon: ArrowUp
  },
  MEDIA: {
    color: 'darkGray',
    background: 'middleGray',
    icon: Minus
  },
  BAJA: {
    color: 'negative',
    background: 'midNegative',
    icon: ArrowDown
  }
};

const PercentLabel = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  .pill-wrapper {
    color: ${({ score }) => select(pillsColors[score].color)};
    background-color: ${({ score }) => select(pillsColors[score].background)};
    display: flex;
    justify-content: center;
    align-items: center;
    width: fit-content;
    height: 16px;
    font-weight: 500;
    font-size: 11px;
    padding: 0 4px;
    border-radius: 12px;
    div {
      width: 9px;
      height: 9px;
      background-size: cover;
      background-image: url(${({ score }) => pillsColors[score].icon});
      margin-right: 3px;
      margin-bottom: 3px;
      ${({ score }) =>
        score === 'MEDIA' &&
        css`
          height: 2px;
        `}
    }
  }
`;

const Loading = styled.div`
  &.dot-flashing {
    position: relative;
    width: 8px;
    height: 8px;
    border-radius: 4px;
    background-color: ${selectColor('reservedOrange')};
    color: ${selectColor('white')};
    animation: ${dotFlashing} 1.5s infinite linear normal;
    animation-delay: 0.1s;
    ::before {
      content: '';
      display: inline-block;
      position: absolute;
      top: 0;
    }
    ::after {
      content: '';
      display: inline-block;
      position: absolute;
      top: 0;
    }
    ::before {
      left: -15px;
      width: 8px;
      height: 8px;
      border-radius: 4px;
      background-color: ${selectColor('reservedOrange')};
      color: ${selectColor('white')};
      animation: ${dotFlashing} 1.5s infinite normal;
    }
    ::after {
      left: 15px;
      width: 8px;
      height: 8px;
      border-radius: 4px;
      background-color: ${selectColor('reservedOrange')};
      color: ${selectColor('white')};
      animation: ${dotFlashing} 1.5s infinite normal;
      animation-delay: 0.5s;
    }
  }
`;

const HeaderTooltipContainer = styled.div`
  display: ${({ isMobile }) => (isMobile ? 'none' : 'inline-flex')};
  align-items: center;
  height: 100%;
  position: relative;
  top: 4px;
  margin-left: ${({ isMobile }) => (isMobile ? 'unset' : '5px')};
`;

const TooltipContent = styled.span`
  width: 100%;
  white-space: pre-wrap;
  display: block;
  text-align: center;
`;

const TitlePriceByM2 = styled.div`
  height: 17px;
  display: flex;
  align-items: center;
  gap: ${({ isMobile }) => (isMobile ? '15px' : '5px')};
`;

export {
  TableLayout,
  TableRow,
  TableData,
  NoData,
  RetryButton,
  Loading,
  LoadingContainer,
  PercentLabel,
  HeaderContent,
  Headers,
  HeaderTooltipContainer,
  TooltipContent,
  TitlePriceByM2
};
